import axios from "axios";
import { clsx, type ClassValue } from "clsx";
import { format } from "date-fns";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Focused inputs the user has not clicked or typed into yet. */
const untouchedSinceFocus = new WeakSet<HTMLInputElement>();

/**
 * Input handlers that select the whole value when the input gains focus, so
 * typing replaces a filled value. A plain `select()` on focus is undone by the
 * click's caret placement (and by a form reset rewriting the value right after
 * a dialog autofocuses the input), so the first click selects everything
 * instead. Once the user clicks or types, clicks place the caret as usual.
 */
export const selectAllOnFocus = {
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
    untouchedSinceFocus.add(e.currentTarget);
  },
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
    untouchedSinceFocus.delete(e.currentTarget);
  },
  onMouseDown: (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const focused = document.activeElement === input;
    if (focused && !untouchedSinceFocus.has(input)) return;

    e.preventDefault();
    input.focus();
    input.select();
    untouchedSinceFocus.delete(input);
  },
};

export const formatDateOnly = (dateString: string) => {
  if (!dateString) return "";
  return format(new Date(dateString + "T12:00:00"), "dd/MM/yyyy");
};

export const markdownExcerpt = (text: string, maxLength = 140): string => {
  if (!text) return "";

  const plain = text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^[-*]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();

  return plain.length > maxLength
    ? `${plain.slice(0, maxLength).trimEnd()}…`
    : plain;
};

export const isDateExpired = (
  dateString: string | null | undefined,
  now: Date = new Date(),
) => {
  if (!dateString) return false;
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const date = new Date(isDateOnly ? `${dateString}T23:59:59` : dateString);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < now.getTime();
};

export const formatToCurrency = (value: number): string => {
  const safeValue = isNaN(value) ? 0 : value;

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeValue);
};

export const formatToBrl = (value: number, fractionDigits = 2): string => {
  const safeValue = isNaN(value) ? 0 : value;

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(safeValue);
};

export function formatNumber(
  value: number | string | null | undefined,
  decimalPlaces = 2,
): string {
  if (value === null || value === undefined || value === "") {
    return "0,00";
  }
  const numValue = typeof value === "string" ? Number.parseFloat(value) : value;
  if (isNaN(numValue)) {
    return "0,00";
  }
  return numValue.toFixed(decimalPlaces).replace(".", ",");
}

export const secondsToTime = (seconds: number) => {
  const isNegative = seconds < 0;
  seconds = Math.abs(seconds);
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const timeString = `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  return isNegative ? `-${timeString}` : timeString;
};

export const errorHandler = (error: unknown, contextMessage: string) => {
  console.error(error);
  let errorMessage = contextMessage + " Por favor, contate o suporte.";

  if (axios.isAxiosError(error)) {
    if (!error.response) {
      // Network error (no response from server)
      errorMessage =
        "Erro de conexão. Verifique sua internet e tente novamente.";
    } else {
      // API error with response
      errorMessage =
        error.response?.data?.message ||
        error.response?.data?.data?.message ||
        contextMessage;
    }
  }

  toast.error(errorMessage);
  return errorMessage;
};

export const isValidCpf = (value: string) => {
  const cpf = value.replace(/\D/g, "");
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  const checkDigit = (length: number) => {
    let sum = 0;
    for (let i = 0; i < length; i++) {
      sum += Number(cpf[i]) * (length + 1 - i);
    }
    return ((sum * 10) % 11) % 10;
  };

  return checkDigit(9) === Number(cpf[9]) && checkDigit(10) === Number(cpf[10]);
};

export const formatCNPJ = (cnpj: string) => {
  if (!cnpj) return "-";

  if (cnpj.includes(".") || cnpj.includes("/")) return cnpj;

  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
};

export const formatPhone = (phone: string) => {
  if (!phone) return "-";
  const numbersOnly = phone.replace(/\D/g, "");

  if (numbersOnly.length <= 10) {
    return numbersOnly
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  } else {
    return numbersOnly
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2");
  }
};

export const toggleVisibility = (
  currentVisibility: boolean,
  setVisibility: (value: boolean) => void,
) => {
  setVisibility(!currentVisibility);
};

export const formatDateTime = (
  dateString: string | null | undefined,
  timeOnly: boolean = false,
): string => {
  if (!dateString) {
    return timeOnly ? "-" : "";
  }

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return timeOnly ? "-" : "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  if (timeOnly) return `${hours}:${minutes}:${seconds}`;

  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
};

export const formatDateTimeWithoutTz = (
  dateTime: string,
  timeOnly: boolean = false,
): string => {
  try {
    const [datePart, timePart] = dateTime.split(/[T ]/);
    const [year, month, day] = datePart.split("-");
    const [hours, minutes, seconds] = timePart.split(":");

    const formattedDate = `${day}/${month}/${year}`;
    const formattedTime = `${hours}:${minutes}:${seconds ? seconds.split(".")[0] : "00"}`;

    if (timeOnly) return formattedTime;

    return `${formattedDate} ${formattedTime}`;
  } catch {
    return dateTime;
  }
};

export const formatDistance = (meters: number) => {
  return (meters / 1000).toFixed(2);
};

/**
 * Formata uma duração em segundos de forma compacta: "1h 5min", "2min 59s", "45s".
 */
export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainingSeconds = total % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
  }

  if (minutes > 0) {
    return remainingSeconds > 0
      ? `${minutes}min ${remainingSeconds}s`
      : `${minutes}min`;
  }

  return `${remainingSeconds}s`;
};

export const formatPhoneNumber = (phone: string | undefined): string => {
  if (!phone) return "";

  const cleaned = phone.replace(/\D/g, "");

  if (cleaned.length > 10) {
    return cleaned.replace(/^(\d{2})(\d{5})(\d{4}).*/, "($1) $2-$3");
  } else if (cleaned.length > 6) {
    return cleaned.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, "($1) $2-$3");
  } else if (cleaned.length > 2) {
    return cleaned.replace(/^(\d{2})(\d{0,4}).*/, "($1) $2");
  } else {
    return cleaned.replace(/^(\d{0,2}).*/, "($1");
  }
};

const PASSWORD_CHAR_POOLS = [
  "abcdefghijklmnopqrstuvwxyz",
  "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  "0123456789",
  "!@#$%&*?",
];

const randomChar = (chars: string) => {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return chars[bytes[0] % chars.length];
};

export const generateStrongPassword = (length = 12): string => {
  const required = PASSWORD_CHAR_POOLS.map(randomChar);
  const allChars = PASSWORD_CHAR_POOLS.join("");
  const remaining = Array.from({ length: length - required.length }, () =>
    randomChar(allChars),
  );

  const passwordChars = [...required, ...remaining];
  for (let i = passwordChars.length - 1; i > 0; i--) {
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);
    const j = bytes[0] % (i + 1);
    [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]];
  }

  return passwordChars.join("");
};

export const isHex = (num: string) => {
  return /^(0x)?[0-9a-f]*[a-f][0-9a-f]*$/i.test(num);
};

export const hexToDecimal = (hex: string) => {
  return BigInt("0x" + hex).toString();
};

export const formatKm = (meters: number, fractionDigits = 2) => {
  const km = meters / 1000;
  return km
    .toFixed(fractionDigits)
    .replace(".", ",")
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
};

interface VehicleOption {
  key: string;
  value: string;
  group?: string;
  isGroupHeader?: boolean;
  disabled?: boolean;
  className?: string;
  style?: object;
}

interface Tag {
  id: number;
  value: string;
  vehicle?: Array<{
    id: number;
    plate: string;
    title: string;
  }>;
}

interface Vehicle {
  id: number;
  plate: string;
  title: string;
}

interface TagAndVehicleResponse {
  data: {
    tags?: Tag[];
    vehicles_with_no_tags?: Vehicle[];
  };
}

export const formatVehicleOptions = (
  response: TagAndVehicleResponse,
): VehicleOption[] => {
  const groupedOptions: VehicleOption[] = [];

  if (response.data.tags && Array.isArray(response.data.tags)) {
    response.data.tags.forEach((tag) => {
      if (tag.vehicle && Array.isArray(tag.vehicle) && tag.vehicle.length > 0) {
        const groupName = `Grupo: ${tag.value}`.toUpperCase();

        groupedOptions.push({
          key: `group-header-${tag.id}`,
          value: ` ---------- ${groupName} (${tag.vehicle.length}) ----------`,
          isGroupHeader: true,
          disabled: true,
          className:
            "font-bold text-blue-600 bg-blue-50 px-3 py-2 text-sm uppercase tracking-wide border-b border-blue-200",
          style: {
            fontWeight: "bold",
            fontSize: "0.875rem",
            color: "#2563eb",
            backgroundColor: "#eff6ff",
            padding: "8px 12px",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            borderBottom: "1px solid #cbd5e1",
          },
        });

        const sortedVehicles = [...tag.vehicle].sort((a, b) =>
          a.plate.localeCompare(b.plate),
        );

        sortedVehicles.forEach((vehicle) => {
          groupedOptions.push({
            key: `${vehicle.id}`,
            value: `${vehicle.plate} - ${vehicle.title}`,
            group: groupName,
            className: "pl-6 py-1 text-gray-700 hover:bg-gray-50",
            style: {
              paddingLeft: "24px",
              padding: "6px 12px 6px 24px",
              color: "#374151",
              fontSize: "0.875rem",
            },
          });
        });
      }
    });
  }

  if (
    response.data.vehicles_with_no_tags &&
    Array.isArray(response.data.vehicles_with_no_tags) &&
    response.data.vehicles_with_no_tags.length > 0
  ) {
    const groupName = "Veículos sem grupo";

    groupedOptions.push({
      key: "group-header-no-tags",
      value: ` ---------- ${groupName.toUpperCase()} (${response.data.vehicles_with_no_tags.length}) ---------- `,
      isGroupHeader: true,
      disabled: true,
    });

    const sortedVehicles = [...response.data.vehicles_with_no_tags].sort(
      (a, b) => a.plate.localeCompare(b.plate),
    );

    sortedVehicles.forEach((vehicle) => {
      groupedOptions.push({
        key: `${vehicle.id}`,
        value: `${vehicle.plate} - ${vehicle.title}`,
        group: groupName,
        className: "pl-6 py-1 text-gray-700 hover:bg-gray-50",
        style: {
          paddingLeft: "24px",
          padding: "6px 12px 6px 24px",
          color: "#374151",
          fontSize: "0.875rem",
        },
      });
    });
  }

  return groupedOptions;
};
